import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Instagram, Calendar, ChevronDown, ChevronUp } from 'lucide-react';

const StatesSection = () => {
    const [currentLanguage, setCurrentLanguage] = useState('en');
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
        setCurrentLanguage(savedLanguage);

        const handleLanguageChange = (event) => {
            const newLanguage = event.detail?.language || 'ar';
            setCurrentLanguage(newLanguage);
        };

        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    const content = {
        ar: {
            title: "احضر فعالية \"عام على النصر\" في ولايتك",
            subtitle: "اختر ولاية إقامتك… وكن جزءًا من هذا اليوم أينما كنت.",
            organizerLabel: "الجهة المنظمة:",
            dateLabel: "التاريخ:",
            registerButton: "👉 اطلع على التفاصيل من هنا",
            followNote: "تابع الجهة المنظمة لمعرفة تفاصيل الفعالية وتغطياتها في ولايتك.",
            showMore: "عرض المزيد من الولايات",
            showLess: "عرض أقل"
        },
        en: {
            title: "Attend \"A Year of Victory\" Event from Your Province",
            subtitle: "Choose your province… and be part of this day wherever you are.",
            organizerLabel: "Organizing Entity:",
            dateLabel: "Date:",
            registerButton: "👉 Register Here",
            followNote: "Follow the organizing entity to learn about event details and coverage in your province.",
            showMore: "Show More Provinces",
            showLess: "Show Less"
        },
        tr: {
            title: "\"Bir Yıl Zafer\" Etkinliğine İlinizden Katılın",
            subtitle: "İlinizi seçin… ve nerede olursanız olun bu günün bir parçası olun.",
            organizerLabel: "Organize Eden Kuruluş:",
            dateLabel: "Tarih:",
            registerButton: "👉 Buradan Kayıt Olun",
            followNote: "İlinizdeki etkinlik detayları ve kapsamı hakkında bilgi edinmek için organize eden kuruluşu takip edin.",
            showMore: "Daha Fazla İl Göster",
            showLess: "Daha Az Göster"
        }
    };

    const states = [
        {
            id: 1,
            name: { ar: "بورصة", en: "Bursa", tr: "Bursa" },
            organizer: { ar: "تجمع الطلبة السوريين في جامعة أولوداغ", en: "Syrian Students Gathering at Uludağ University", tr: "Suriyeli Öğrenciler Uludağ Üniversitesi Topluluğu" },
            logo: "/assets/logos/oludag.jpg",
            instagram: "https://www.instagram.com/ulusukut/",
            date: "06.12.2025"
        },
        {
            id: 2,
            name: { ar: "سكاريا", en: "Sakarya", tr: "Sakarya" },
            organizer: { ar: "اتحاد الطلبة السوريين في سكاريا", en: "Syrian Students Union in Sakarya", tr: "Sakarya Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/skaria.jpg",
            instagram: "https://www.instagram.com/sob.sau/",
            date: "08.12.2025"
        },
        {
            id: 3,
            name: { ar: "قونيا", en: "Konya", tr: "Konya" },
            organizer: { ar: "تجمع الطلبة السوريين في قونيا", en: "Syrian Students Gathering in Konya", tr: "Konya Suriyeli Öğrenciler Topluluğu" },
            logo: "/assets/logos/konya.jpg",
            instagram: "https://www.instagram.com/sotkonya/",
            date: "06.12.2025"
        },
        {
            id: 4,
            name: { ar: "غازي عنتاب", en: "Gaziantep", tr: "Gaziantep" },
            organizer: { ar: "اتحاد الطلبة السوريين في غازي عنتاب", en: "Syrian Students Union in Gaziantep", tr: "Gaziantep Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/gazientab.jpg",
            instagram: "https://www.instagram.com/s.b.g.syrian"
        },
        {
            id: 5,
            name: { ar: "مرسين", en: "Mersin", tr: "Mersin" },
            organizer: { ar: "اتحاد الطلبة السوريين في مرسين", en: "Syrian Students Union in Mersin", tr: "Mersin Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/marsin.jpg",
            instagram: "https://www.instagram.com/mersin_uniarap",
            date:"10.12.2025"
        },
        {
            id: 6,
            name: { ar: "كوتاهيا", en: "Kütahya", tr: "Kütahya" },
            organizer: { ar: "اتحاد الطلبة السوريين - كوتاهيا", en: "Syrian Students Union - Kütahya", tr: "Suriyeli Öğrenciler Birliği - Kütahya" },
            logo: "/assets/logos/kotahia.jpg",
            instagram: "https://www.instagram.com/sobkutahya",
            date: "07.12.2025"
        },
        {
            id: 7,
            name: { ar: "اسبارطة", en: "Isparta", tr: "Isparta" },
            organizer: { ar: "اتحاد الطلبة السوريين في مدينة اسبارطة", en: "Syrian Students Union in Isparta", tr: "Isparta Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/isbarta.jpg",
            instagram: "https://www.instagram.com/sobisparta"
        },
        {
            id: 8,
            name: { ar: "الفرات", en: "Elazığ", tr: "Elazığ" },
            organizer: { ar: "تجمع الطلاب العرب في جامعة الفرات", en: "Arab Students Gathering at Fırat University", tr: "Fırat Üniversitesi Arap Öğrenciler Topluluğu" },
            logo: "/assets/logos/firat.jpg",
            instagram: "https://www.instagram.com/aot_firat",
            date: "08.12.2025"
        },
        {
            id: 9,
            name: { ar: "كلس", en: "Kilis", tr: "Kilis" },
            organizer: { ar: "الاتحاد العام لطلبة سوريا - فرع كلس", en: "General Union of Syrian Students - Kilis Branch", tr: "Suriye Öğrenciler Genel Birliği - Kilis Şubesi" },
            logo: "/assets/logos/killis.jpg",
            instagram: "https://www.instagram.com/sob.kilis"
        },
        {
            id: 10,
            name: { ar: "إزمير", en: "Izmir", tr: "İzmir" },
            organizer: { ar: "إتحاد الطلبة السوريين في إزمير", en: "Syrian Students Union in Izmir", tr: "İzmir Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/izmir.jpg",
            instagram: "https://www.instagram.com/sob.izm/",
            date: "07.12.2025"
        },
        {
            id: 11,
            name: { ar: "كوجالي", en: "Kocaeli", tr: "Kocaeli" },
            organizer: { ar: "اتحاد الطلبة السوريين في كوجالي", en: "Syrian Students Union in Kocaeli", tr: "Kocaeli Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/kojali.jpg",
            instagram: "https://www.instagram.com/kou.sob/",
            date: "15.12.2025"
        },
        {
            id: 12,
            name: { ar: "دنيزلي", en: "Denizli", tr: "Denizli" },
            organizer: { ar: "اتحاد الطلبة السوريين في جامعة باموكالي", en: "Syrian Students Union at Pamukkale University", tr: "Pamukkale Üniversitesi Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/denizli.jpg",
            instagram: "https://www.instagram.com/sob.pau_",
            date: "07.12.2025"
        },
        {
            id: 13,
            name: { ar: "بولو", en: "Bolu", tr: "Bolu" },
            organizer: { ar: "اتحاد الطلبة السوريين في بولو", en: "Syrian Students Union in Bolu", tr: "Bolu Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/polo.jpg",
            instagram: "https://www.instagram.com/sob.bolu",
            date: "08.12.2025"
        },
        {
            id: 14,
            name: { ar: "كاستامونو", en: "Kastamonu", tr: "Kastamonu" },
            organizer: { ar: "اتحاد الطلبة السوريين - كاستمونو", en: "Syrian Students Union - Kastamonu", tr: "Suriyeli Öğrenciler Birliği - Kastamonu" },
            logo: "/assets/logos/kastanomo.jpg",
            instagram: "https://www.instagram.com/ssu.kastamonu",
            date: "08.12.2025"
        },
        {
            id: 15,
            name: { ar: "أوشاك", en: "Uşak", tr: "Uşak" },
            organizer: { ar: "اتحاد الطلبة السوريين في جامعة اوشاك", en: "Syrian Students Union at Uşak University", tr: "Uşak Üniversitesi Suriyeli Öğrenciler Birliği" },
            logo: "/assets/logos/ucak.jpg",
            instagram: "https://www.instagram.com/sotusak",
            date: "08.12.2025"
        }
    ];

    const text = content[currentLanguage];
    const displayedStates = showAll ? states : states.slice(0, 6);

    return (
        <section id="states" className="py-16 lg:py-24 px-4 bg-background">
            <div className="max-w-[1600px] mx-auto">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
                        {text.title}
                    </h2>
                    <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto">
                        {text.subtitle}
                    </p>
                </motion.div>

                {/* States Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
                    <AnimatePresence mode="popLayout">
                        {displayedStates.map((state, index) => (
                            <motion.div
                                key={state.id}
                                layout
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.3 }}
                                className="bg-card border border-border rounded-lg p-6 shadow-memorial hover:shadow-memorial-lg transition-all duration-300 hover:-translate-y-1"
                            >
                                {/* State Name & Date */}
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-6 h-6 text-primary" />
                                        <h3 className="text-2xl font-bold text-primary">
                                            {state.name[currentLanguage]}
                                        </h3>
                                    </div>
                                </div>

                                {state.date && (
                                    <div className="flex items-center gap-2 mb-4 bg-primary/5 p-2 rounded-lg w-fit">
                                        <Calendar className="w-5 h-5 text-accent" />
                                        <span className="font-semibold text-primary">
                                            {state.date}
                                        </span>
                                    </div>
                                )}

                                {/* Organizer Logo */}
                                <div className="flex justify-center mb-4">
                                    <img 
                                        src={state.logo} 
                                        alt={state.organizer[currentLanguage]}
                                        className="w-34 h-34 rounded-full object-cover border-2 border-primary/20"
                                    />
                                </div>

                                {/* Organizer Info */}
                                <div className="mb-4 text-center">
                                    <p className="text-sm text-text-secondary mb-1">
                                        {text.organizerLabel}
                                    </p>
                                    <p className="text-base font-semibold text-text">
                                        {state.organizer[currentLanguage]}
                                    </p>
                                </div>

                                {/* Register Button */}
                                <a
                                    href={state.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full"
                                >
                                    <button className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 group">
                                        <Instagram className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                        <span>{text.registerButton}</span>
                                    </button>
                                </a>

                                {/* Follow Note */}
                                <p className="text-xs text-text-secondary mt-3 text-center leading-relaxed">
                                    {text.followNote}
                                </p>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>

                {/* Show More/Less Button */}
                {states.length > 6 && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-center"
                    >
                        <button
                            onClick={() => setShowAll(!showAll)}
                            className="flex items-center gap-2 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold py-3 px-8 rounded-full transition-all duration-300 shadow-md hover:shadow-lg group"
                        >
                            <span>{showAll ? text.showLess : text.showMore}</span>
                            {showAll ? (
                                <ChevronUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
                            ) : (
                                <ChevronDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
                            )}
                        </button>
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default StatesSection;
